import { useEffect, useRef, useState } from "react";
import DashboardUI from "@/assets/images/partner/dashboard.png";
import RequirementUI from "@/assets/images/partner/requirement.png";
const workflowSteps = [
  {
    id: 1,
    title: "Partner Access",
    description:
      "Secure login to your dedicated dashboard. All your active projects, past submissions, and team members in one place.",
    image: DashboardUI,
    icon: "ri-shield-keyhole-line",
  },
  {
    id: 2,
    title: "Submit Requirements",
    description:
      "Drag and drop your DWG files, CAD drawings, or project specifications. Our intelligent system validates formats instantly.",
    image: RequirementUI,
    icon: "ri-upload-cloud-2-line",
  },
  {
    id: 3,
    title: "Proposal Review",
    description:
      "Receive detailed proposals with transparent pricing, timeline estimates, and scope breakdown. Approve with one click.",
    image:
      "https://placehold.co/1400x900",
    icon: "ri-file-list-3-line",
  },
  {
    id: 4,
    title: "Project Kickoff",
    description:
      "Track real-time progress with milestone indicators. Payment processing is seamless and secure through our integrated system.",
    image:
      "https://placehold.co/1400x900",
    icon: "ri-rocket-line",
  },
  {
    id: 5,
    title: "Direct Collaboration",
    description:
      "Chat directly with our engineering team. Share feedback, request revisions, and get instant responses without email chains.",
    image:
      "https://placehold.co/1400x900",
    icon: "ri-message-3-line",
  },
  {
    id: 6,
    title: "Asset Delivery",
    description:
      "Download final renders, IES files, and photometric reports. All assets are archived and accessible anytime.",
    image:
      "https://placehold.co/1400x900",
    icon: "ri-download-cloud-line",
  },
];

export default function WorkflowSection() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const sectionTop = sectionRef.current.offsetTop;
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      stepsRef.current.forEach((stepEl, index) => {
        if (!stepEl) return;

        const stepTop = stepEl.offsetTop + sectionTop;
        const stepBottom = stepTop + stepEl.offsetHeight;

        if (scrollPosition >= stepTop && scrollPosition < stepBottom) {
          setActiveStep(index);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            The{" "}
            <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              Workflow
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Six seamless steps from submission to delivery. Watch how each phase
            unfolds.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Scrolling Timeline */}
          <div className="space-y-32">
            {workflowSteps.map((step, index) => (
              <div
                key={step.id}
                ref={(el) => {
                  stepsRef.current[index] = el;
                }}
                className={`transition-all duration-500 ${activeStep === index ? "opacity-100" : "opacity-40"
                  }`}
              >
                <div className="flex items-start gap-6">
                  <div
                    className={`flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-500 ${activeStep === index
                        ? "bg-gradient-to-br from-orange-500 to-red-500 shadow-lg shadow-orange-500/50"
                        : "bg-gray-800/50 border border-gray-700"
                      }`}
                  >
                    <i
                      className={`${step.icon} text-2xl ${activeStep === index ? "text-white" : "text-gray-500"
                        }`}
                    ></i>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className={`text-sm font-bold ${activeStep === index
                            ? "text-orange-500"
                            : "text-gray-600"
                          }`}
                      >
                        STEP {step.id}
                      </span>
                      {activeStep === index && (
                        <div className="h-px flex-1 bg-gradient-to-r from-orange-500 to-transparent"></div>
                      )}
                    </div>

                    <h3 className="text-3xl font-bold mb-4">{step.title}</h3>
                    <p className="text-lg text-gray-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Side - Sticky Monitor */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="relative">
              {/* Monitor Frame with Glassmorphism */}
              <div className="relative rounded-2xl overflow-hidden border border-gray-800/50 bg-gradient-to-br from-gray-900/40 to-gray-800/40 backdrop-blur-xl shadow-2xl">
                {/* Monitor Header */}
                <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-800/50 bg-black/20">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                  </div>
                  <div className="flex-1 text-center">
                    <span className="text-sm text-gray-500 font-medium">
                      DMI Partner Portal
                    </span>
                  </div>
                </div>

                {/* Screen Content */}
                <div className="relative aspect-[16/10] bg-black overflow-hidden">
                  {workflowSteps.map((step, index) => (
                    <div
                      key={step.id}
                      className={`absolute inset-0 transition-opacity duration-700 ${activeStep === index
                          ? "opacity-100 z-10"
                          : "opacity-0 z-0"
                        }`}
                    >
                      <div className="w-full h-full">
                        <img
                          src={step.image}
                          alt={step.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Overlay Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-orange-500/10 via-transparent to-transparent pointer-events-none"></div>
                    </div>
                  ))}
                </div>

                {/* Monitor Base Glow */}
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-3/4 h-8 bg-orange-500/20 blur-2xl rounded-full"></div>
              </div>

              {/* Step Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {workflowSteps.map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => {
                      const stepEl = stepsRef.current[index];
                      if (stepEl && sectionRef.current) {
                        const sectionTop = sectionRef.current.offsetTop;
                        const stepTop = stepEl.offsetTop + sectionTop;
                        window.scrollTo({
                          top: stepTop - 200,
                          behavior: "smooth",
                        });
                      }
                    }}
                    className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${activeStep === index
                        ? "w-12 bg-gradient-to-r from-orange-500 to-red-500"
                        : "w-2 bg-gray-700 hover:bg-gray-600"
                      }`}
                  ></button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
